/**
 * AI Chat React Query Hooks
 *
 * Provides data fetching and mutation hooks for AI conversation and insight operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { aiService } from '../services/ai-service';

/**
 * Query key factory for AI queries
 */
export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...aiKeys.all, 'conversation', id] as const,
  insight: (page: string) => [...aiKeys.all, 'insight', page] as const,
  transactionInsight: (txId: string) => [...aiKeys.all, 'txn-insight', txId] as const,
};

/** Fetch paginated list of conversations */
export function useConversations() {
  return useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: () => aiService.listConversations(),
    staleTime: 30_000,
  });
}

/** Fetch a single conversation with messages */
export function useConversation(id: string | null) {
  return useQuery({
    queryKey: aiKeys.conversation(id!),
    queryFn: () => aiService.getConversation(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

/** Create a new conversation */
export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => aiService.createConversation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
    },
  });
}

/** Delete a conversation */
export function useDeleteConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiService.deleteConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversations() });
    },
  });
}

/** Submit feedback on a message */
export function useSubmitFeedback(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, feedback }: { messageId: string; feedback: -1 | 0 | 1 }) =>
      aiService.submitFeedback(messageId, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.conversation(conversationId) });
    },
  });
}

/** Fetch AI insight for a page (cached 24 hours) */
export function usePageInsight(page: string, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.insight(page),
    queryFn: () => aiService.getPageInsight(page),
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
  });
}

/** Fetch AI insight for a transaction (cached 7 days) */
export function useTransactionInsight(txId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.transactionInsight(txId!),
    queryFn: () => aiService.getTransactionInsight(txId!),
    enabled: txId !== null && enabled,
    staleTime: 7 * 24 * 60 * 60 * 1000,
  });
}
